import React, { useState, useEffect } from 'react';
import { User, DollarSign, ChevronDown, ChevronUp, LogOut, History, Download } from 'lucide-react';
import { getApiUrl, getAuthHeaders, clearAuth } from '../utils/auth';
import { formatIdentificationDisplay } from '../utils/formatIdentification';
import { generatePaymentHistoryStatementPDF } from '../utils/pdfGenerator';
import PdfPreviewModal from './PdfPreviewModal';

const InvestorDashboard = ({ user, onLogout }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedEnrollments, setExpandedEnrollments] = useState(() => new Set());
  const [downloadingStatement, setDownloadingStatement] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const closePdfPreview = () => {
    if (pdfPreviewUrl) URL.revokeObjectURL(pdfPreviewUrl);
    setPdfPreviewUrl(null);
    setShowPreviewModal(false);
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setError('');
        const res = await fetch(`${getApiUrl()}/api/investor-auth/me`, { headers: getAuthHeaders() });
        const result = await res.json();
        if (result.success) setProfile(result.data);
        else setError(result.message || 'Failed to load profile');
      } catch (err) {
        console.error('Fetch profile error:', err);
        setError('Unable to load your information');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(amount);

  const formatDate = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short', day: 'numeric' })
      : '—';

  const formatMonthYear = (dateString) =>
    dateString
      ? new Date(dateString).toLocaleDateString('en-MY', { year: 'numeric', month: 'short' })
      : '—';

  const handleLogout = () => {
    clearAuth();
    onLogout();
  };

  if (loading) {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen neo-page flex items-center justify-center p-4">
        <div className="neo-raised p-8 max-w-md text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error || 'Profile not found'}</p>
          <button
            onClick={handleLogout}
            className="px-4 py-2 neo-btn-danger"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen neo-page transition-colors duration-200">
      {/* Header */}
      <header className="neo-page">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/images/YDG_LOGO.png" alt="YDG" className="h-10 w-auto object-contain dark:invert" />
            <span className="text-lg font-semibold text-gray-900 dark:text-dark-text">Investor Portal</span>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 neo-btn-danger"
          >
            <LogOut size={18} />
            Sign out
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text mb-6">
          Welcome back, {profile.name}
        </h1>

        {/* Personal info */}
        <section className="neo-raised p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text mb-4 flex items-center gap-2">
            <User size={20} />
            Personal information
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-dark-muted block">Name</span>
              <span className="font-medium text-gray-900 dark:text-dark-text">{profile.name}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-dark-muted block">Email</span>
              <span className="font-medium text-gray-900 dark:text-dark-text">{profile.email}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-dark-muted block">Phone</span>
              <span className="font-medium text-gray-900 dark:text-dark-text">{profile.phone || '—'}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-dark-muted block">Identification Number</span>
              <span className="font-medium text-gray-900 dark:text-dark-text">{profile.icNumber ? formatIdentificationDisplay(profile.icNumber) : '—'}</span>
            </div>
          </div>
        </section>

        {/* Enrollments */}
        <section className="neo-raised overflow-hidden">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text p-6 pb-0 flex items-center gap-2">
            <DollarSign size={20} />
            My enrollments
          </h2>
          <div className="p-6">
            {!profile.enrollments?.length ? (
              <p className="text-gray-500 dark:text-dark-muted">No enrollments yet.</p>
            ) : (
              <div className="space-y-4">
                {profile.enrollments.map((enr) => (
                  <div
                    key={enr.id}
                    className="neo-inset overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => {
                        setExpandedEnrollments((prev) => {
                          const next = new Set(prev);
                          if (next.has(enr.id)) next.delete(enr.id);
                          else next.add(enr.id);
                          return next;
                        });
                      }}
                      className="w-full flex items-center justify-between p-4 text-left bg-gray-50 dark:bg-slate-800/50 hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <div>
                        <span className="font-medium text-gray-900 dark:text-dark-text">
                          {enr.productDetails?.product?.name}
                        </span>
                        <span className="text-gray-500 dark:text-dark-muted ml-2">
                          — {formatCurrency(enr.amount)}
                        </span>
                        {enr.jointName && (
                          <div className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                            Joint name: {enr.jointName}
                          </div>
                        )}
                      </div>
                      {expandedEnrollments.has(enr.id) ? (
                        <ChevronUp className="text-gray-400" size={20} />
                      ) : (
                        <ChevronDown className="text-gray-400" size={20} />
                      )}
                    </button>
                    {expandedEnrollments.has(enr.id) && (
                      <div className="p-4 pt-0 border-t border-gray-200 dark:border-dark-border space-y-3 text-sm">
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block">Amount</span>
                            <span className="font-medium text-gray-900 dark:text-dark-text">{formatCurrency(enr.amount)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block">Payment mode</span>
                            <span className="font-medium text-gray-900 dark:text-dark-text">{enr.productDetails?.paymentMode ?? '—'}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block">Start date</span>
                            <span className="font-medium text-gray-900 dark:text-dark-text">{formatDate(enr.startDate)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block">End date</span>
                            <span className="font-medium text-gray-900 dark:text-dark-text">{formatDate(enr.endDate)}</span>
                          </div>
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block">Bank</span>
                            <span className="font-medium text-gray-900 dark:text-dark-text">{enr.bankName} {enr.bankAccountNumber}</span>
                          </div>
                        </div>
                        {enr.dividendPayments?.length > 0 && (
                          <div>
                            <span className="text-gray-500 dark:text-dark-muted block mb-2">Dividend schedule</span>
                            <div className="overflow-x-auto max-h-48 overflow-y-auto">
                              <table className="w-full text-xs">
                                <thead className="bg-gray-50 dark:bg-slate-800/50 sticky top-0">
                                  <tr>
                                    <th className="text-left py-2 px-2 font-medium text-gray-500">Year</th>
                                    <th className="text-left py-2 px-2 font-medium text-gray-500">Due date</th>
                                    <th className="text-center py-2 px-2 font-medium text-gray-500">Amount</th>
                                    <th className="text-center py-2 px-2 font-medium text-gray-500">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                                  {enr.dividendPayments.map((d, i) => (
                                    <tr key={i}>
                                      <td className="py-1.5 px-2 text-gray-900 dark:text-dark-text">Year {d.year}</td>
                                      <td className="py-1.5 px-2 text-gray-900 dark:text-dark-text">{formatMonthYear(d.dueDate)}</td>
                                      <td className="py-1.5 px-2 text-center text-gray-900 dark:text-dark-text">{formatCurrency(d.amount)}</td>
                                      <td className="py-1.5 px-2 text-center">
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          d.status === 'Paid' ? 'bg-green-100 text-green-800' :
                                          d.status === 'Overdue' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                        }`}>
                                          {d.status}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Payment history / Statement (paid dividends by enrollment) */}
        <section className="neo-raised overflow-hidden mt-6">
          <div className="p-6 pb-0 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-text flex items-center gap-2">
                <History size={20} />
                Payment Received History
              </h2>
              <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
                Payments we have made to you, by enrollment.
              </p>
            </div>
            <button
              type="button"
              disabled={downloadingStatement}
              onClick={async () => {
                setDownloadingStatement(true);
                setError('');
                try {
                  const url = await generatePaymentHistoryStatementPDF(profile);
                  if (url) {
                    setPdfPreviewUrl(url);
                    setShowPreviewModal(true);
                  }
                } catch (err) {
                  console.error('Preview statement error:', err);
                  setError('Failed to generate PDF. Please try again.');
                } finally {
                  setDownloadingStatement(false);
                }
              }}
              className="flex items-center gap-2 px-4 py-2 neo-btn-primary disabled:opacity-70 disabled:cursor-not-allowed text-sm font-medium"
            >
              {downloadingStatement ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Generating…
                </>
              ) : (
                <>
                  <Download size={18} />
                  Preview statement
                </>
              )}
            </button>
          </div>
          <div className="p-6">
            {!profile.enrollments?.length ? (
              <p className="text-gray-500 dark:text-dark-muted">No enrollments yet.</p>
            ) : (
              <div className="space-y-6">
                {profile.enrollments.map((enr) => {
                  const paidDividends = (enr.dividendPayments || []).filter((d) => d.status === 'Paid' && d.paidDate);
                  return (
                    <div key={enr.id} className="neo-inset overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
                        <span className="font-medium text-gray-900 dark:text-dark-text">
                          {enr.productDetails?.product?.name}
                        </span>
                        <span className="text-gray-500 dark:text-dark-muted ml-2">
                          — {formatCurrency(enr.amount)}
                        </span>
                        {enr.jointName && (
                          <div className="text-xs text-primary-600 dark:text-primary-400 mt-0.5">
                            Joint name: {enr.jointName}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        {paidDividends.length === 0 ? (
                          <p className="text-sm text-gray-500 dark:text-dark-muted">No payments received yet for this enrollment.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 dark:bg-slate-800/50">
                                <tr>
                                  <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-dark-muted">Year</th>
                                  <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-dark-muted">Due date</th>
                                  <th className="text-center py-2 px-2 font-medium text-gray-500 dark:text-dark-muted">Amount</th>
                                  <th className="text-left py-2 px-2 font-medium text-gray-500 dark:text-dark-muted">Paid date</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
                                {paidDividends.map((d, i) => (
                                  <tr key={i}>
                                    <td className="py-2 px-2 text-gray-900 dark:text-dark-text">Year {d.year}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-dark-text">{formatMonthYear(d.dueDate)}</td>
                                    <td className="py-2 px-2 text-center text-gray-900 dark:text-dark-text">{formatCurrency(d.amount)}</td>
                                    <td className="py-2 px-2 text-gray-900 dark:text-dark-text">{formatDate(d.paidDate)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      </main>

      <PdfPreviewModal
        open={showPreviewModal && !!pdfPreviewUrl}
        pdfUrl={pdfPreviewUrl}
        title="Payment statement preview"
        downloadFilename="payment-statement.pdf"
        onClose={closePdfPreview}
      />
    </div>
  );
};

export default InvestorDashboard;
